/* Free shipping progress bar - Paolina Roma
 *
 * <pr-shipping-bar> and the money helpers it needs, extracted verbatim from
 * assets/section-main-cart.js so the cart DRAWER can use the same element as the
 * cart PAGE. section-main-cart.js is NOT edited: it keeps its own copy and its own
 * `if (!customElements.get('pr-shipping-bar'))` guard, so whichever file runs first
 * defines the element and the other one steps aside. With cart_type = 'drawer' this
 * file wins everywhere: layout/theme.liquid loads it in <head>, before <main> and
 * therefore before the section script in defer order.
 *
 * The zone (Italy / Europe / rest of the world) and the copy are resolved server-side
 * in snippets/pcd-progress-bar.liquid; this only converts the euro threshold into the
 * presentment currency, fills the bar and writes the remaining-amount message.
 *
 * TWO deliberate differences from the copy in section-main-cart.js, both documented on
 * the lines themselves:
 *   1. the host element also gets an `is-unlocked` class, because the drawer's unlocked
 *      state is a visual flip (blush card, check glyph) that CSS cannot infer from an
 *      inline width;
 *   2. the threshold LABEL is written here too, from the same converted figure as the
 *      countdown. Liquid's money filters format in the presentment currency but never
 *      convert, so a server-rendered label would have read "$80.00" next to a countdown
 *      heading for ~$88 on a USD market. One arithmetic, one format, one row.
 * Neither is selected by the cart page's CSS or JS (`.pr-shipping-progress__threshold`
 * simply does not exist there), so the page behaves exactly as before whichever copy of
 * the element ends up winning.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ money */

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'number' ? precision : 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number === null) return '0';

    var value = (number / 100.0).toFixed(precision);
    var parts = value.split('.');
    var units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return units + cents;
  }

  /* shop.money_format can carry markup and HTML entities (&euro;). It travels in
     a data attribute, so the browser decodes it once; tags and any remaining
     entity are cleaned here, otherwise the message would read "&euro;12,00". */
  function cleanFormat(raw) {
    if (!raw) return '';
    var stripped = String(raw).replace(/<[^>]*>/g, '');
    var decoder = document.createElement('textarea');
    decoder.innerHTML = stripped;
    return decoder.value;
  }

  function formatMoney(cents, format) {
    if (!format) return '';
    var match = format.match(PLACEHOLDER);
    if (!match) return '';

    var value;
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        return '';
    }

    return format.replace(PLACEHOLDER, value);
  }

  /* --------------------------------------------------------- currency rate */

  function cartRoute() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /* Thresholds are stored in euros; cart.total_price arrives in the presentment
     currency. One lookup per page, cached at module level. */
  var ratePromise = null;

  function getCurrencyRate() {
    if (ratePromise) return ratePromise;

    var injected = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
    var parsed = parseFloat(injected);

    if (parsed > 0) {
      ratePromise = Promise.resolve(parsed);
      return ratePromise;
    }

    ratePromise = fetch(cartRoute() + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        return parseFloat(data.currency_rate) || 1;
      })
      .catch(function () {
        return 1;
      });

    return ratePromise;
  }

  /* ------------------------------------------------------ <pr-shipping-bar> */

  class PrShippingBar extends HTMLElement {
    connectedCallback() {
      var fill = this.querySelector('.pr-shipping-progress__fill');
      var message = this.querySelector('.pr-shipping-progress__message');
      var label = this.querySelector('.pr-shipping-progress__threshold');
      var total = parseInt(this.dataset.total, 10) || 0;
      var thresholdUnits = parseFloat(this.dataset.threshold) || 0;
      var labelTpl = this.dataset.msgThreshold || '';
      var incomplete = this.dataset.msgIncomplete || '';
      var complete = this.dataset.msgComplete || '';
      var format = cleanFormat(this.dataset.moneyFormat);
      /* ADDED here, absent from the copy in section-main-cart.js: the drawer paints
         a different card (blush fill, check glyph) once the threshold is cleared, and
         CSS cannot read an inline width. Nothing on the cart page selects .is-unlocked,
         so the page is unaffected whichever copy of the element wins. */
      var host = this;

      /* No usable money format, or no figure to show: the sentence goes out without
         the token rather than with a wrong or empty amount. */
      function fillToken(tpl, token, amount) {
        if (!tpl) return '';
        if (amount) return tpl.replace(token, amount);
        return tpl
          .replace(token, '')
          .replace(/\s{2,}/g, ' ')
          .trim();
      }

      if (thresholdUnits <= 0) {
        if (fill) fill.style.width = '100%';
        if (message) message.textContent = complete;
        if (label) label.textContent = fillToken(labelTpl, '{threshold}', '');
        host.classList.add('is-unlocked');
        return;
      }

      getCurrencyRate().then(function (rate) {
        var threshold = thresholdUnits * 100 * rate;
        var remaining = threshold - total;
        host.classList.toggle('is-unlocked', remaining <= 0);
        var progress = Math.min((total / threshold) * 100, 100);

        /* The 100ms delay is Motion's: it lets the 0% start frame paint so the
           width transition is visible on load. */
        if (fill) {
          window.setTimeout(function () {
            fill.style.width = progress + '%';
          }, 100);
        }

        /* Same converted figure as the countdown below, same formatMoney. */
        if (label) {
          label.textContent = fillToken(labelTpl, '{threshold}', formatMoney(Math.round(threshold), format));
        }

        if (!message) return;

        if (remaining <= 0) {
          message.textContent = complete;
          return;
        }

        message.textContent = fillToken(incomplete, '[amount]', formatMoney(Math.round(remaining), format));
      });
    }
  }

  if (!customElements.get('pr-shipping-bar')) {
    customElements.define('pr-shipping-bar', PrShippingBar);
  }
})();
